import { useState, useCallback } from 'react';
import LogicFlow from '@logicflow/core';

interface EdgeOperationsProps {
  lfRef: React.MutableRefObject<LogicFlow | null>;
  updateGraphData: () => void;
  setGlobalElementOrder: React.Dispatch<React.SetStateAction<number>>;
  setElementAddHistory: React.Dispatch<
    React.SetStateAction<
      Array<{
        id: string;
        type: 'node' | 'edge';
        timestamp: number;
      }>
    >
  >;
}

interface PendingConnection {
  source: string;
  target: string;
  sourceAnchorId?: string;
  targetAnchorId?: string;
  startPoint?: { x: number; y: number };
  endPoint?: { x: number; y: number };
}

export const useOPMEdgeOperations = ({
  lfRef,
  updateGraphData,
  setGlobalElementOrder,
  setElementAddHistory,
}: EdgeOperationsProps) => {
  // 边类型选择弹窗状态
  const [showEdgeTypeDialog, setShowEdgeTypeDialog] = useState(false);
  const [pendingConnection, setPendingConnection] = useState<PendingConnection>(
    {
      source: '',
      target: '',
      sourceAnchorId: '',
      targetAnchorId: '',
      startPoint: undefined,
      endPoint: undefined,
    }
  );
  const [editingEdge, setEditingEdge] = useState<any>(null);
  const [isEditingExistingEdge, setIsEditingExistingEdge] = useState(false);

  // 用于跟踪程序创建的边ID，避免拦截
  const [programCreatedEdgeIds, setProgramCreatedEdgeIds] = useState<
    Set<string>
  >(new Set());

  // 处理边双击事件
  const handleEdgeDoubleClick = useCallback((edgeData: any) => {
    console.log('处理边双击:', edgeData);
    setEditingEdge(edgeData);
    setIsEditingExistingEdge(true);
    setShowEdgeTypeDialog(true);
  }, []);

  // 确认边类型选择
  const handleEdgeTypeConfirm = useCallback(
    (config: { edgeType: string; edgeText: string }) => {
      if (!lfRef.current) return;

      console.log('🔗 确认边类型选择:', config);
      console.log('🔗 当前连接信息:', pendingConnection);
      console.log('🔗 是否编辑现有边:', isEditingExistingEdge);

      try {
        // 获取当前图数据
        const currentGraphData = lfRef.current.getGraphData() as {
          nodes: any[];
          edges: any[];
        };
        console.log('🔗 当前图数据:', currentGraphData);

        if (isEditingExistingEdge && editingEdge) {
          // 编辑现有边的情况
          console.log('🔗 修改现有边配置:', editingEdge.id, '新配置:', config);

          // 确定最终的边类型 - 默认使用折线，如果选择了自定义箭头类型则使用自定义类型
          let finalType = 'polyline'; // 默认使用折线

          // 如果选择了自定义箭头类型，使用自定义类型
          if (config.edgeType !== 'arrow') {
            finalType = config.edgeType;
          }

          console.log('🔗 边类型映射:', {
            edgeType: config.edgeType,
            finalType: finalType,
          });

          // 确保边文字是字符串格式
          const edgeText =
            typeof config.edgeText === 'string' ? config.edgeText.trim() : '';
          console.log('🔗 设置边文字:', edgeText, '类型:', typeof edgeText);

          // 找到要修改的边并更新其配置
          const updatedEdges = currentGraphData.edges.map((edge: any) => {
            if (edge.id === editingEdge.id) {
              return {
                ...edge,
                type: finalType,
                text: edgeText, // 直接使用字符串
                properties: {
                  ...edge.properties,
                  edgeType: config.edgeType, // 箭头类型
                },
              };
            }
            return edge;
          });

          // 重新渲染图形
          const newGraphData = {
            nodes: currentGraphData.nodes,
            edges: updatedEdges,
          };

          // 标记这是程序创建的边，避免被拦截
          setProgramCreatedEdgeIds(prev => {
            const newSet = new Set(prev);
            newSet.add(editingEdge.id);
            return newSet;
          });

          lfRef.current.render(newGraphData);
          console.log('🔗 边配置修改完成:', editingEdge.id);

          // 更新图数据
          setTimeout(() => {
            updateGraphData();
          }, 100);
        } else {
          // 创建新边的情况
          console.log('🔗 创建新边:', pendingConnection, config);

          // 确定最终的边类型 - 默认使用折线，如果选择了自定义箭头类型则使用自定义类型
          let finalType = 'polyline'; // 默认使用折线

          // 如果选择了自定义箭头类型，使用自定义类型
          if (config.edgeType !== 'arrow') {
            finalType = config.edgeType;
          }

          console.log('🔗 新边类型映射:', {
            edgeType: config.edgeType,
            finalType: finalType,
          });

          // 确保边文字是字符串格式
          const edgeText =
            typeof config.edgeText === 'string' ? config.edgeText.trim() : '';
          console.log('🔗 创建新边文字:', edgeText, '类型:', typeof edgeText);

          const edgeId = `custom_edge_${Date.now()}`;

          const newEdge = {
            id: edgeId,
            sourceNodeId: pendingConnection.source,
            targetNodeId: pendingConnection.target,
            type: finalType,
            text: edgeText, // 直接使用字符串
            // 包含锚点信息以确保连线位置准确
            sourceAnchorId: pendingConnection.sourceAnchorId,
            targetAnchorId: pendingConnection.targetAnchorId,
            // 如果有具体的起终点坐标，也包含进去
            ...(pendingConnection.startPoint && {
              startPoint: pendingConnection.startPoint,
            }),
            ...(pendingConnection.endPoint && {
              endPoint: pendingConnection.endPoint,
            }),
            properties: {
              edgeType: config.edgeType, // 箭头类型
            },
          };

          console.log('🔗 准备创建自定义边:', newEdge);

          // 先记录边到添加历史
          setGlobalElementOrder(prevOrder => {
            const newOrder = prevOrder + 1;
            setElementAddHistory(prev => {
              if (!prev.find(h => h.id === edgeId)) {
                console.log('记录新边到添加历史:', edgeId, '顺序:', newOrder);
                return [
                  ...prev,
                  {
                    id: edgeId,
                    type: 'edge',
                    timestamp: newOrder,
                  },
                ];
              }
              return prev;
            });
            return newOrder;
          });

          // 使用render方法重新渲染整个图，包含新边
          const newGraphData = {
            nodes: currentGraphData.nodes,
            edges: [...currentGraphData.edges, newEdge],
          };

          // 标记这是程序创建的边，避免被拦截
          setProgramCreatedEdgeIds(prev => {
            const newSet = new Set(prev);
            newSet.add(edgeId);
            return newSet;
          });

          lfRef.current.render(newGraphData);
          console.log('🔗 自定义边创建完成:', newEdge);

          // 立即更新图数据以触发OPL更新
          setTimeout(() => {
            updateGraphData();
          }, 100);
        }
      } catch (error) {
        console.error('🔗 处理边配置时出错:', error);
      }

      // 重置状态
      setShowEdgeTypeDialog(false);
      setPendingConnection({
        source: '',
        target: '',
        sourceAnchorId: '',
        targetAnchorId: '',
        startPoint: undefined,
        endPoint: undefined,
      });
      setEditingEdge(null);
      setIsEditingExistingEdge(false);
    },
    [
      lfRef,
      pendingConnection,
      isEditingExistingEdge,
      editingEdge,
      updateGraphData,
      setGlobalElementOrder,
      setElementAddHistory,
    ]
  );

  // 取消边类型选择
  const handleEdgeTypeCancel = useCallback(() => {
    console.log('取消边类型选择');
    setShowEdgeTypeDialog(false);
    setPendingConnection({
      source: '',
      target: '',
      sourceAnchorId: '',
      targetAnchorId: '',
      startPoint: undefined,
      endPoint: undefined,
    });
    setEditingEdge(null);
    setIsEditingExistingEdge(false);
  }, []);

  // 检查两个节点是否在同一分组内
  const areNodesInSameGroup = useCallback(
    (sourceId: string, targetId: string) => {
      if (!lfRef.current) return false;

      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };

      const sourceNode = graphData.nodes.find(node => node.id === sourceId);
      const targetNode = graphData.nodes.find(node => node.id === targetId);

      if (!sourceNode || !targetNode) return false;

      // 检查状态节点或值节点（圆角矩形节点）
      const isSourceStateOrValue =
        sourceNode.type === 'state' || sourceNode.type === 'value';
      const isTargetStateOrValue =
        targetNode.type === 'state' || targetNode.type === 'value';
      if (!isSourceStateOrValue || !isTargetStateOrValue) return false;

      // 检查两个节点是否属于同一个父分组
      const sourceParentGroup = sourceNode.properties?.parentGroupId;
      const targetParentGroup = targetNode.properties?.parentGroupId;

      return (
        sourceParentGroup &&
        targetParentGroup &&
        sourceParentGroup === targetParentGroup
      );
    },
    [lfRef]
  );

  // 检查节点是否为分组节点
  const isGroupNode = useCallback(
    (nodeId: string) => {
      if (!lfRef.current) return false;

      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };

      const node = graphData.nodes.find(node => node.id === nodeId);
      return node?.type === 'dynamic-group';
    },
    [lfRef]
  );

  // 检查节点是否为某个分组的子状态节点
  const isChildStateNode = useCallback(
    (nodeId: string, groupId: string) => {
      if (!lfRef.current) return false;

      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };

      const node = graphData.nodes.find(node => node.id === nodeId);
      return node?.properties?.parentGroupId === groupId;
    },
    [lfRef]
  );

  // 处理边添加事件
  const handleEdgeAdd = useCallback(
    (edgeData: any) => {
      console.log('🔗 检测到边创建尝试:', edgeData);

      // 检查是否是程序创建的边
      if (programCreatedEdgeIds.has(edgeData.id)) {
        console.log('🔗 程序创建的边，记录到历史:', edgeData.id);

        // 从程序创建的边ID集合中移除
        setProgramCreatedEdgeIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(edgeData.id);
          return newSet;
        });

        // 记录边到添加历史
        setGlobalElementOrder(prevOrder => {
          const newOrder = prevOrder + 1;
          setElementAddHistory(prev => {
            if (!prev.find(h => h.id === edgeData.id)) {
              console.log('记录边到添加历史:', edgeData.id, '顺序:', newOrder);
              return [
                ...prev,
                {
                  id: edgeData.id,
                  type: 'edge',
                  timestamp: newOrder,
                },
              ];
            }
            return prev;
          });
          return newOrder;
        });

        // 延迟更新缓存，确保边已经完全添加
        setTimeout(() => {
          updateGraphData();
        }, 100);
        return;
      }

      // 用户手动创建的边，需要验证连线规则
      const { sourceNodeId, targetNodeId } = edgeData;

      // 调试日志：输出连接信息
      console.log('🔗 连线验证:', {
        sourceNodeId,
        targetNodeId,
        sourceIsGroup: isGroupNode(sourceNodeId),
        targetIsGroup: isGroupNode(targetNodeId),
        sourceIsChildOfTarget: isChildStateNode(sourceNodeId, targetNodeId),
        targetIsChildOfSource: isChildStateNode(targetNodeId, sourceNodeId),
        sameGroup: areNodesInSameGroup(sourceNodeId, targetNodeId),
      });

      // === 连线限制规则 ===

      // 获取图数据用于规则验证
      const graphData = lfRef.current!.getGraphData() as {
        nodes: any[];
        edges: any[];
      };

      // 规则0：节点不能连接自己（自环）
      if (sourceNodeId === targetNodeId) {
        console.log('🚫 规则0：阻止节点连接自己');
        lfRef.current?.deleteEdge(edgeData.id);
        return;
      }

      // 获取源节点和目标节点信息
      const sourceNode = graphData.nodes.find(n => n.id === sourceNodeId);
      const targetNode = graphData.nodes.find(n => n.id === targetNodeId);
      const sourceParentGroup = sourceNode?.properties?.parentGroupId;
      const targetParentGroup = targetNode?.properties?.parentGroupId;

      // 规则1：同一组内的状态节点不能互相连线
      if (areNodesInSameGroup(sourceNodeId, targetNodeId)) {
        console.log('🚫 规则1：阻止同一组内状态节点互相连线');
        lfRef.current?.deleteEdge(edgeData.id);
        return;
      }

      // 规则2：分组节点不能连接到其子状态节点（双向限制）
      if (
        (isGroupNode(sourceNodeId) &&
          isChildStateNode(targetNodeId, sourceNodeId)) ||
        (isChildStateNode(sourceNodeId, targetNodeId) &&
          isGroupNode(targetNodeId))
      ) {
        console.log('🚫 规则2：阻止分组节点与子状态节点之间的双向连接');
        lfRef.current?.deleteEdge(edgeData.id);
        return;
      }

      // 检查是否临时禁用弹窗（程序重建连线时）
      if ((window as any).tempDisableEdgeDialog) {
        console.log('🔗 程序重建连线，跳过弹窗直接创建');
        // 直接记录到历史，不弹窗
        setGlobalElementOrder(prevOrder => {
          const newOrder = prevOrder + 1;
          setElementAddHistory(prev => {
            if (!prev.find(h => h.id === edgeData.id)) {
              return [
                ...prev,
                { id: edgeData.id, type: 'edge', timestamp: newOrder },
              ];
            }
            return prev;
          });
          return newOrder;
        });
        setTimeout(() => updateGraphData(), 100);
        return;
      }

      // 用户手动创建的边，需要拦截并显示类型选择弹窗
      console.log('🔗 用户手动创建的边，拦截并显示弹窗');
      console.log('🔗 边数据详情:', edgeData);

      // 保存完整的连接信息，包括锚点ID和坐标
      const connectionInfo: PendingConnection = {
        source: edgeData.sourceNodeId,
        target: edgeData.targetNodeId,
        sourceAnchorId: edgeData.sourceAnchorId,
        targetAnchorId: edgeData.targetAnchorId,
        // 如果有起终点坐标，也保存
        ...(edgeData.startPoint && { startPoint: edgeData.startPoint }),
        ...(edgeData.endPoint && { endPoint: edgeData.endPoint }),
      };

      console.log('🔗 保存的连接信息:', connectionInfo);

      // 立即删除这条临时边，因为我们要先选择类型
      lfRef.current?.deleteEdge(edgeData.id);

      // 显示边类型选择弹窗
      setPendingConnection(connectionInfo);
      setShowEdgeTypeDialog(true);
    },
    [
      lfRef,
      programCreatedEdgeIds,
      updateGraphData,
      setGlobalElementOrder,
      setElementAddHistory,
      areNodesInSameGroup,
      isGroupNode,
      isChildStateNode,
    ]
  );

  // 处理边删除事件
  const handleEdgeDelete = useCallback(
    (edgeData: any) => {
      console.log('边删除:', edgeData);

      // 如果删除的是正在编辑的边，关闭边类型选择弹窗
      setEditingEdge((prev: any) => {
        if (prev && prev.id === edgeData.id) {
          console.log('🎯 删除的是正在编辑的边，关闭边类型弹窗');
          setShowEdgeTypeDialog(false);
          setPendingConnection({ source: '', target: '' });
          setIsEditingExistingEdge(false);
          return null;
        }
        return prev;
      });

      // 从添加历史中移除该边
      setElementAddHistory(prev => {
        const edgeIndex = prev.findIndex(
          h => h.id === edgeData.id && h.type === 'edge'
        );
        if (edgeIndex !== -1) {
          console.log('从添加历史中移除边:', edgeData.id);
          return prev.filter((_, index) => index !== edgeIndex);
        }
        return prev;
      });

      updateGraphData();
    },
    [updateGraphData, setElementAddHistory]
  );

  // 添加程序创建的边ID到跟踪集合
  const addProgramCreatedEdgeId = useCallback((edgeId: string) => {
    setProgramCreatedEdgeIds(prev => {
      const newSet = new Set(prev);
      newSet.add(edgeId);
      return newSet;
    });
  }, []);

  return {
    // 状态
    showEdgeTypeDialog,
    pendingConnection,
    editingEdge,
    isEditingExistingEdge,
    programCreatedEdgeIds,

    // 事件处理函数
    handleEdgeDoubleClick,
    handleEdgeTypeConfirm,
    handleEdgeTypeCancel,
    handleEdgeAdd,
    handleEdgeDelete,
    addProgramCreatedEdgeId,

    // 状态设置函数
    setShowEdgeTypeDialog,
    setPendingConnection,
    setEditingEdge,
    setIsEditingExistingEdge,
  };
};
